import * as React from "react";
import Grid from "@mui/material/Grid";
import Title from "@/components/molecules/Title";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { OperationPlansList } from "@/components/operationPlans/OperationPlansList";
import {
  OperationPlanOrder,
  OperationPlanSortKey,
  OperationPlansPerEvCar,
  SavedState,
} from "@/models/operationPlans";
import { OperationPlansConstansts } from "./OperationPlansConstansts";
import Paper from "@/components/atoms/Paper";
import { Picker } from "@/components/atoms/Picker";
import { AutoCompleteMultiple } from "@/components/molecules/AutoCompleteMultiple";
import { Office, ONLY_CONTRACT } from "@/models/office";
import {
  getOperationPlansAPI,
  getOperationPlansCountAPI,
} from "@/util/api/operationPlans";
import { getLoginUserAPI, getOfficesAPI } from "@/util/api/API";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { MULTI_OFFICE_ID } from "@/common/CommonConstants";
import Cookies from "js-cookie";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import { EDIT_USER, GX_ADMIN } from "@/models/auth";
import { getJSTCurrentTime } from "@/util/datetime";
import { getOfficeOptionLabel } from "@/util";

const PAGE_SIZE = 30;

export const OperationPlansPage = () => {
  const { navigate, savedState } = useStatefulNavigator<SavedState>();

  const [emptyResult, setEmptyResult] = React.useState(false);

  const [dateError, setDateError] = React.useState(false);

  const [total, setTotal] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(0);
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [order, setOrder] = React.useState<OperationPlanOrder>({
    officeName: "asc",
    vehicleCode: "asc",
  });

  const [operationPlans, setOperationPlans] = React.useState<
    OperationPlansPerEvCar[]
  >([]);
  const [officesSelected, setOfficesSelected] = React.useState<Office[]>([]);
  const [targetYm, setTargetYm] = React.useState(
    new Date(getJSTCurrentTime("date")).toISOString().slice(0, 7)
  );
  const [activeMonth, setActiveMonth] = React.useState("");
  const [clickable, setClickable] = React.useState(false);

  const thisMonth = React.useMemo(
    () => getJSTCurrentTime("date").slice(0, 7),
    []
  );
  const today = React.useMemo(() => new Date(getJSTCurrentTime("date")), []);
  const [focusDate, setFocusDate] = React.useState<Date>(today);

  const todaysColumn = React.useRef<HTMLElement | null>(null);

  const updatePlanList = React.useCallback(
    (
      targetYm: string,
      page: number,
      order: OperationPlanOrder,
      officeIds?: number[]
    ) => {
      const ym = targetYm.replace("-", "/");
      getOperationPlansCountAPI(ym, officeIds).then((res) => {
        setTotal(res.data ?? 0);
        setPage(page);
        setEmptyResult(res.data === 0);
      });
      getOperationPlansAPI(ym, page, PAGE_SIZE, order, officeIds).then(
        (res) => {
          setOperationPlans(res.data ?? []);
          setActiveMonth(targetYm);
          setFocusDate(
            thisMonth === targetYm
              ? today
              : new Date(new Date(targetYm).setDate(1))
          );
        }
      );
    },
    [thisMonth, today]
  );

  const handleChangeOffices = React.useCallback((offices: Office[]) => {
    setOfficesSelected(offices);
    Cookies.set(MULTI_OFFICE_ID, offices.map(({ id }) => id).join(","));
  }, []);

  const handleChangeTargetYm = React.useCallback(
    (value?: string | undefined, inRange?: boolean | undefined) => {
      setDateError(false);
      setTargetYm(value ?? "");
    },
    []
  );

  const handleSearchData = React.useCallback(() => {
    if (!targetYm) {
      setDateError(true);
      return;
    }

    updatePlanList(
      targetYm,
      1,
      order,
      officesSelected.map(({ id }) => id)
    );
  }, [officesSelected, order, targetYm, updatePlanList]);

  const handleResetSearchCondition = React.useCallback(() => {
    setOfficesSelected([]);
    setTargetYm(new Date(getJSTCurrentTime("date")).toISOString().slice(0, 7));
  }, []);

  const handleChangeSort = React.useCallback(
    (key: OperationPlanSortKey) => {
      const nextOrder = {
        [key]: ["asc", undefined].includes(order[key]) ? "desc" : "asc",
      };

      updatePlanList(
        targetYm,
        page,
        nextOrder,
        officesSelected.map(({ id }) => id)
      );

      setOrder(nextOrder);
    },
    [order, updatePlanList, targetYm, page, officesSelected]
  );

  const handleChangePage = React.useCallback(
    (page: number) => {
      updatePlanList(
        targetYm,
        page,
        order,
        officesSelected.map(({ id }) => id)
      );
    },
    [officesSelected, order, targetYm, updatePlanList]
  );

  const handleClickPlan = React.useCallback(
    (planId: number) => {
      navigate(`/operation_plans/${planId}/edit`, {
        officesSelected,
        targetYm,
        page,
        order,
      });
    },
    [navigate, officesSelected, order, page, targetYm]
  );

  React.useEffect(() => {
    getLoginUserAPI().then((res) => {
      setClickable([GX_ADMIN, EDIT_USER].includes(res?.data?.role ?? -1));
    });
    getOfficesAPI(ONLY_CONTRACT)
      .then(({ data }) => {
        setOffices(data ?? []);

        if (savedState?.officesSelected != null) {
          setOfficesSelected(savedState.officesSelected);
          setTargetYm(savedState.targetYm);
          setPage(savedState.page);
          setOrder(savedState.order);
          updatePlanList(
            savedState.targetYm,
            savedState.page,
            savedState.order,
            savedState.officesSelected.map(({ id }) => id)
          );
          return;
        }

        const cookieSaved =
          Cookies.get(MULTI_OFFICE_ID)
            ?.split(",")
            .filter((id) => id != null && id !== "")
            .map((id) => Number(id)) ?? [];
        const filterOffices = (data ?? []).filter(({ id }) =>
          cookieSaved.includes(id)
        );
        setOfficesSelected(filterOffices);
        if (filterOffices.length) {
          const order: OperationPlanOrder = {
            officeName: "asc",
            vehicleCode: "asc",
          };
          const targetYm = new Date(getJSTCurrentTime("date"))
            .toISOString()
            .slice(0, 7);

          setOrder(order);
          setTargetYm(targetYm);
          updatePlanList(
            targetYm,
            1,
            order,
            filterOffices.map(({ id }) => id)
          );
        }
      })
      .catch((e) =>
        console.log("===Error occurred while getting offices===", e)
      );
  }, [
    savedState?.officesSelected,
    savedState?.order,
    savedState?.page,
    savedState?.targetYm,
    updatePlanList,
  ]);

  React.useEffect(() => {
    let ignore = false;

    // 今日の日付のカラムが車両稼働予定に存在したら
    if (
      !ignore &&
      todaysColumn.current != null &&
      todaysColumn.current.scrollIntoView != null
    ) {
      // その要素が画面横方向の中央に位置するまでスクロールする
      todaysColumn.current.scrollIntoView({
        behavior: "auto",
        block: "end",
        inline: "center",
      });
    }

    return () => {
      ignore = true;
    };
  }, [operationPlans]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {OperationPlansConstansts.OperationPlan}
          </Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={6}>
                  <AutoCompleteMultiple
                    options={offices}
                    limit={100}
                    getOptionLabel={getOfficeOptionLabel}
                    value={officesSelected}
                    onChange={handleChangeOffices}
                    uniqueKey="id"
                    label={OperationPlansConstansts.OfficeName}
                    forSearch
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  md={2}
                  lg={2}
                  sx={{ pt: "0!important" }}
                  className="required"
                >
                  <Picker
                    type="month"
                    label={OperationPlansConstansts.TargetDate}
                    parse={(input) => input}
                    decode={(dateStr) => dateStr.slice(0, 7)}
                    value={targetYm}
                    onChange={handleChangeTargetYm}
                    error={dateError}
                    forSearch
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  md={4}
                  lg={4}
                  container
                  justifyContent="flex-end"
                >
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleSearchData}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    sx={{ ml: 2 }}
                    onClickBtn={handleResetSearchCondition}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={10} md={10} lg={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <OperationPlansList
                operationPlans={operationPlans}
                targetMonth={activeMonth}
                page={page}
                total={total}
                size={PAGE_SIZE}
                orderType={order}
                clickable={clickable}
                focusDate={focusDate}
                focusDateColumnRef={todaysColumn}
                onChangePage={handleChangePage}
                onClickChangeSort={handleChangeSort}
                onClickPlan={handleClickPlan}
              />
              {emptyResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
